import { getUsageStatus } from "./budget";
import { DEFAULT_SCORE_TEMPLATES } from "./customerValueCalculation";
import { saveDraftResponse } from "./departmentWorkflow";
import { getPackageEndDate, getPackageLimit } from "./packageLimits";
import { getDefaultRecommendedSolution } from "./recommendedSolutions";
import type {
  CustomerValueCriterion,
  CustomerValueModel,
  CustomerValueScoreTemplate,
  Department,
  DepartmentResponse,
  Organization,
  User,
  Workspace,
  AdminAuditLog,
  WorkspaceAiSummary,
  WorkspaceUsage
} from "../types";

export const organizations: Organization[] = [
  {
    id: "org-hauffe-demo",
    name: "Demo Manufacturing GmbH",
    industry: "B2B Manufacturing",
    companySize: "250–500 Mitarbeitende"
  }
];

export const users: User[] = [
  {
    id: "user-hauffe-admin",
    email: "torben@hauffe.io",
    firstName: "Torben",
    lastName: "Hauffe",
    role: "hauffe_admin",
    isActive: true
  },
  {
    id: "user-owner-demo",
    email: "owner@example.com",
    firstName: "Mara",
    lastName: "Beispiel",
    role: "company_owner",
    organizationId: "org-hauffe-demo",
    isActive: true
  },
  {
    id: "user-sales-demo",
    email: "sales@example.com",
    firstName: "Jonas",
    lastName: "Sales",
    role: "department_contributor",
    organizationId: "org-hauffe-demo",
    isActive: true
  }
];

const packageType = "guided_digital_access_3m" as const;
const startDate = "2026-07-01";
const packageLimit = getPackageLimit(packageType);

export const workspaces: Workspace[] = [
  {
    id: "workspace-customer-value-demo",
    organizationId: "org-hauffe-demo",
    name: "Customer Value Workspace",
    packageType,
    startDate,
    endDate: getPackageEndDate(startDate, packageType),
    status: "draft",
    goal: "Eine gemeinsame Customer-Value-Logik für Sales, Marketing, Delivery und Finance vorbereiten.",
    focusType: "both",
    maxDepartments: packageLimit.maxDepartments,
    createdByUserId: "user-hauffe-admin"
  }
];

export const departments: Department[] = [
  {
    id: "dept-sales",
    workspaceId: "workspace-customer-value-demo",
    name: "Sales",
    participantEmail: "sales@example.com",
    participantUserId: "user-sales-demo",
    status: "started",
    inviteToken: "demo-sales-token"
  },
  {
    id: "dept-delivery",
    workspaceId: "workspace-customer-value-demo",
    name: "Delivery",
    participantEmail: "delivery@example.com",
    participantUserId: undefined,
    status: "invited",
    inviteToken: "demo-delivery-token"
  },
  {
    id: "dept-finance",
    workspaceId: "workspace-customer-value-demo",
    name: "Finance",
    participantEmail: "finance@example.com",
    participantUserId: undefined,
    status: "submitted",
    inviteToken: "demo-finance-token"
  }
];

export const departmentResponses: DepartmentResponse[] = [
  saveDraftResponse({
    workspaceId: "workspace-customer-value-demo",
    departmentId: "dept-sales",
    userId: "user-sales-demo",
    answers: {
      valuable_customer_definition: "Kunden mit wiederkehrendem Bedarf und klarer Entscheidungsstruktur.",
      high_effort_customers: "Kunden mit vielen Sonderabstimmungen und wechselnden Ansprechpartnern."
    },
    now: "2026-07-02T08:00:00.000Z"
  }),
  {
    ...saveDraftResponse({
      workspaceId: "workspace-customer-value-demo",
      departmentId: "dept-finance",
      answers: {
        valuable_customer_definition: "Kunden mit hoher Marge, sauberer Zahlungsqualität und planbarer Auslastung.",
        high_effort_customers: "Kunden mit niedriger Marge, vielen Sonderwünschen und schwacher Planbarkeit.",
        strategic_fit: "Kunden, die wiederkehrende Umsätze mit guter Deckungsbeitragsqualität ermöglichen.",
        bad_fit_signals: "Späte Zahlungen, unklare Anforderungen und hohe interne Eskalationshäufigkeit.",
        priority_customers: "Kunden mit hohem Deckungsbeitrag und geringer Betreuungskomplexität.",
        deprioritize_customers: "Opportunities mit hoher Komplexität und geringer Marge.",
        missing_criteria: "Betreuungsaufwand und Zahlungsqualität sollten früher sichtbar sein.",
        department_specific_view: "Finance sieht häufig früh, ob Umsatz wirklich wirtschaftlichen Beitrag leistet."
      },
      now: "2026-07-02T09:00:00.000Z"
    }),
    responseStatus: "submitted",
    submittedAt: "2026-07-02T10:00:00.000Z"
  }
];

const techCostEstimateEur = 18.5;

export const workspaceUsage: WorkspaceUsage = {
  workspaceId: "workspace-customer-value-demo",
  aiInputTokensUsed: 0,
  aiOutputTokensUsed: 0,
  aiCallsUsed: 0,
  aiCostEstimateEur: 0,
  cloudflareCostEstimateEur: 1.35,
  techCostEstimateEur,
  techBudgetLimitEur: packageLimit.techBudgetLimitEur,
  usageStatus: getUsageStatus(techCostEstimateEur, packageLimit.techBudgetLimitEur)
};

export const workspaceAiSummaries: WorkspaceAiSummary[] = [
  {
    id: "summary-demo",
    workspaceId: "workspace-customer-value-demo",
    summaryType: "department_synthesis",
    inputResponseIds: ["response_dept-finance"],
    result: {
      workspace_summary:
        "Finance sieht Customer Value vor allem dort, wo Marge, Zahlungsqualität, Planbarkeit und geringer Betreuungsaufwand zusammenkommen.",
      department_summaries: [
        {
          department_id: "dept-finance",
          department_name: "Finance",
          summary:
            "Finance betont wirtschaftlichen Beitrag, Deckungsbeitragsqualität, Zahlungsqualität und Planbarkeit.",
          main_value_criteria: ["Marge", "Zahlungsqualität", "Planbarkeit"],
          main_effort_signals: ["Sonderwünsche", "Eskalationen", "Hohe Komplexität"],
          risks_seen_by_department: ["Umsatz ohne wirtschaftlichen Beitrag", "Spät sichtbare Profitabilitätsrisiken"]
        }
      ],
      customer_value_criteria_suggestions: [
        {
          name: "Deckungsbeitragsqualität",
          description: "Bewertet, ob Umsatz auch nachhaltigen wirtschaftlichen Beitrag leistet.",
          type: "positive",
          source_departments: ["Finance"],
          reason: "Finance benennt Marge und wirtschaftlichen Beitrag als zentrale Customer-Value-Signale."
        }
      ],
      positive_customer_signals: ["Hohe Marge", "Gute Zahlungsqualität", "Wiederkehrender Bedarf"],
      negative_customer_signals: ["Viele Sonderwünsche", "Schwache Planbarkeit", "Hohe Eskalationshäufigkeit"],
      decision_friction_signals: [
        {
          signal: "Umsatz und wirtschaftlicher Beitrag können unterschiedlich bewertet werden.",
          departments_involved: ["Finance", "Sales"],
          why_it_matters: "Wenn Umsatz priorisiert wird, ohne Marge und Aufwand sichtbar zu machen, entsteht Entscheidungsreibung."
        }
      ],
      open_clarification_questions: [
        "Welche Mindestmarge soll für Höchste Wertspanne gelten?",
        "Welche Aufwandssignale sollten Sales vor Angebotsabgabe kennen?"
      ],
      next_step_recommendation:
        "Die Kriterien sollten mit Sales und Delivery abgeglichen und in eine erste Customer Value Matrix überführt werden.",
      recommended_solution: getDefaultRecommendedSolution()
    },
    model: "gpt-5-mini",
    inputTokens: 900,
    outputTokens: 700,
    costEstimateEur: 0.001625,
    createdByUserId: "user-hauffe-admin",
    createdAt: "2026-07-02T10:05:00.000Z",
    updatedAt: "2026-07-02T10:05:00.000Z"
  }
];

export const customerValueModelsSeed: CustomerValueModel[] = [
  {
    id: "cv-model-demo",
    workspaceId: "workspace-customer-value-demo",
    name: "Customer Value Calculation",
    status: "draft",
    sourceAiSummaryId: "summary-demo",
    createdByUserId: "user-owner-demo",
    createdAt: "2026-07-02T10:10:00.000Z",
    updatedAt: "2026-07-02T10:10:00.000Z"
  }
];

export const customerValueCriteriaSeed: CustomerValueCriterion[] = [
  {
    id: "criterion-margin-quality",
    modelId: "cv-model-demo",
    workspaceId: "workspace-customer-value-demo",
    name: "Deckungsbeitragsqualität",
    description: "Bewertet, ob Umsatz nachhaltigen wirtschaftlichen Beitrag leistet.",
    criterionType: "positive",
    weight: 25,
    sortOrder: 1,
    isActive: true,
    source: "ai_suggested",
    createdAt: "2026-07-02T10:10:00.000Z",
    updatedAt: "2026-07-02T10:10:00.000Z"
  },
  {
    id: "criterion-payment-quality",
    modelId: "cv-model-demo",
    workspaceId: "workspace-customer-value-demo",
    name: "Zahlungsqualität",
    description: "Bewertet Planbarkeit, Verlässlichkeit und wirtschaftliche Stabilität der Kundenbeziehung.",
    criterionType: "positive",
    weight: 15,
    sortOrder: 2,
    isActive: true,
    source: "ai_suggested",
    createdAt: "2026-07-02T10:10:00.000Z",
    updatedAt: "2026-07-02T10:10:00.000Z"
  },
  {
    id: "criterion-support-effort",
    modelId: "cv-model-demo",
    workspaceId: "workspace-customer-value-demo",
    name: "Betreuungsaufwand",
    description: "Bewertet interne Belastung, Sonderwünsche und Koordinationsaufwand.",
    criterionType: "negative",
    weight: 20,
    sortOrder: 3,
    isActive: true,
    source: "ai_suggested",
    createdAt: "2026-07-02T10:10:00.000Z",
    updatedAt: "2026-07-02T10:10:00.000Z"
  }
];

export const customerValueScoreTemplatesSeed: CustomerValueScoreTemplate[] = DEFAULT_SCORE_TEMPLATES.map(
  (template, index) => ({
    id: `score-template-demo-${index + 1}`,
    modelId: "cv-model-demo",
    workspaceId: "workspace-customer-value-demo",
    ...template,
    createdAt: "2026-07-02T10:10:00.000Z",
    updatedAt: "2026-07-02T10:10:00.000Z"
  })
);

export const adminAuditLogs: AdminAuditLog[] = [];
